import Component from "@base/js/component";
import Chips from "@components/chips/chips";

export default class Calendar extends Component {
  // Defines the element selector which will initialize this component
  static selector = ".calendar";

  // Defines children that needs to be queried as part of this component
  static children = {
    prevButton: ".calendar__prev-month", // Queries a single element
    nextButton: ".calendar__next-month", // Queries a an array of elements
    fromRange: ".calendar__input--from",
    toRange: ".calendar__input--to",
    submitCal: ".calendar__submit",
  };

  // Defines sub components that needs to be queried as part of this component
  static members = {
    chips: Chips,
  };

  months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  daysOfWeek = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];

  gridsize = 42;

  dateState = {
    month: new Date().getMonth(),
    year: new Date().getFullYear(),
  };

  datePlaceholder = "MM/DD/YYYY";

  // Defines event handlers to attach to children
  eventHandlers = {
    prevButton: {
      click: this.handlePrevClick,
    },
    nextButton: {
      click: this.handleNextClick,
    },
    fromRange: {
      focus: this.focusOnField,
      input: this.handleInputChange,
    },
    toRange: {
      focus: this.focusOnField,
      input: this.handleInputChange,
    },
  };

  // Will be called upon component instantiation
  afterInit() {
    // Show the current month by default
    this.showCalendar();
    this.highlightDateRange();
  }

  generateDatesForGrid(year, month) {
    const dates = [];
    const firstDay = new Date(year, month).getDay();
    const totalDaysInMonth = new Date(year, month + 1, 0).getDate();
    const totalDaysInPrevMonth = new Date(year, month, 0).getDate();

    // Show days from prev month
    for (let i = 1; i <= firstDay; i++) {
      const prevMonthDate = totalDaysInPrevMonth - firstDay + i;
      const key = new Date(
        this.dateState.year,
        this.dateState.month - 1,
        prevMonthDate
      );
      dates.push({
        key,
        date: prevMonthDate,
        monthClass: "calendar__date--prev",
      });
    }
    // Show days of the current month
    const today = new Date();
    for (let i = 1; i <= totalDaysInMonth; i++) {
      const key = new Date(this.dateState.year, this.dateState.month, i);
      if (
        i === today.getDate() &&
        this.dateState.month === today.getMonth() &&
        this.dateState.year === today.getFullYear()
      ) {
        dates.push({
          key,
          date: i,
          monthClass: "calendar__date--current calendar__date--today",
        });
      } else {
        dates.push({ key, date: i, monthClass: "calendar__date--current" });
      }
    }

    // If there is space left over in the grid, then show the days for the next month
    if (dates.length < this.gridsize) {
      const count = this.gridsize - dates.length;
      for (let i = 1; i <= count; i++) {
        const key = new Date(this.dateState.year, this.dateState.month + 1, i);
        dates.push({ key, date: i, monthClass: "calendar__date--next" });
      }
    }
    return dates;
  }

  highlightDateRange() {
    const fromField = this.element.querySelector(
      ".calendar__input--from"
    ).value;
    const toField = this.element.querySelector(".calendar__input--to").value;

    if (fromField && toField) {
      const fromFieldToDate = new Date(fromField);
      const toFieldToDate = new Date(toField);

      const fromGridItem = this.element.querySelector(
        `.calendar__date--current[data-calendar-date='${fromFieldToDate}']`
      );
      const toGridItem = this.element.querySelector(
        `.calendar__date--current[data-calendar-date='${toFieldToDate}']`
      );

      if (fromGridItem !== null) {
        fromGridItem.classList.add("calendar__date--selected");
      }

      if (toGridItem !== null) {
        toGridItem.classList.add("calendar__date--selected");
      }

      if (fromFieldToDate > toFieldToDate) {
        return;
      }

      const dateDiff = (toFieldToDate - fromFieldToDate) / (1000 * 3600 * 24);

      const iterateDate = fromFieldToDate;
      for (let ctr = 1; ctr < dateDiff; ctr++) {
        iterateDate.setDate(iterateDate.getDate() + 1);
        const iterateGridItem = this.element.querySelector(
          `.calendar__date--current[data-calendar-date='${iterateDate}']`
        );
        if (iterateGridItem !== null) {
          iterateGridItem.classList.add("calendar__date--in-between");
        }
      }
    }
  }

  handleInputChange() {
    this.removeDateRangeHighlight();

    const dateformat = /^(0[1-9]|1[0-2])[/](0[1-9]|[1-2][0-9]|3[01])[/]\d{4}$/;
    const focusedFieldVal = this.element.querySelector(
      ".calendar__input--focus"
    ).value;

    if (focusedFieldVal.match(dateformat)) {
      const focusedFieldDate = new Date(focusedFieldVal);

      this.changeCalendar(
        focusedFieldDate.getMonth(),
        focusedFieldDate.getFullYear()
      ); // navigate calendar to inputted month

      const focusFieldToDate = new Date(focusedFieldVal);
      const focusedGridItem = this.element.querySelector(
        `.calendar__date--current[data-calendar-date='${focusFieldToDate}']`
      );
      if (focusedGridItem !== null) {
        focusedGridItem.classList.add("calendar__date--selected");
      }

      this.highlightDateRange();
    }
  }

  highlightFromOrTo(event) {
    this.removeSelectedDateHighlight();
    this.removeDateRangeHighlight();

    const formatDate = new Date(event.target.dataset.calendarDate);
    const formatDateMonthPlus = formatDate.getMonth() + 1;
    const formatDateMonth = formatDateMonthPlus.toString().padStart(2, "0");
    const formatDateDay = formatDate.getDate().toString().padStart(2, "0");
    const formatDateYear = formatDate.getFullYear();

    let focusedField = this.element.querySelector(".calendar__input--focus");
    if (focusedField === null) {
      focusedField = this.element.querySelector(".calendar__input--from");
    }
    focusedField.classList.add("calendar__input--focus");
    focusedField.value = `${formatDateMonth}/${formatDateDay}/${formatDateYear}`;

    const currentFromDate = this.element.querySelector(
      ".calendar__input--from"
    ).value;
    const currentToDate = this.element.querySelector(
      ".calendar__input--to"
    ).value;

    if (focusedField.dataset.rangeField === "from") {
      if (
        currentToDate &&
        new Date(currentToDate) < new Date(focusedField.value)
      ) {
        focusedField.value = this.datePlaceholder;
      }
    } else if (
      currentFromDate &&
      new Date(currentFromDate) > new Date(focusedField.value)
    ) {
      focusedField.value = this.datePlaceholder;
    }

    event.target.classList.add("calendar__date--selected");

    this.highlightDateRange();
  }

  handleDateClicks() {
    const activeDates = this.element.querySelectorAll(
      ".calendar__date--current"
    );

    activeDates.forEach((date) => {
      date.addEventListener("click", this.highlightFromOrTo.bind(this));
    });
  }

  handlePrevClick(event) {
    event.preventDefault();
    this.showCalendar(-1);
    this.highlightDateRange();
  }

  handleNextClick(event) {
    event.preventDefault();
    this.showCalendar(1);
    this.highlightDateRange();
  }

  renderCalendar() {
    const calendarPicker = this.element.querySelector(".calendar__picker");
    const calendarTitle = this.element.querySelector(".calendar__nav h3");

    const stateMonth = this.dateState.month;
    const stateYear = this.dateState.year;

    // Build the calendar picker
    calendarTitle.textContent = `${this.months[stateMonth]} ${stateYear}`;
    calendarPicker.innerHTML = `
        <div class='calendar__grid'>
          ${this.daysOfWeek
            .map((day) => `<div class="calendar__grid-title">${day}</div>`)
            .join("")}
          ${this.generateDatesForGrid(stateYear, stateMonth)
            .map(
              (date) =>
                `<div data-calendar-date="${date.key}" class="calendar__date ${date.monthClass}">${date.date}</div>`
            )
            .join("")}
        </div>
    `;

    this.handleDateClicks();
  }

  showCalendar(prevNextIndicator = 0) {
    const date = new Date(
      this.dateState.year,
      this.dateState.month + prevNextIndicator
    );
    // Update the state
    this.dateState.year = date.getFullYear();
    this.dateState.month = date.getMonth();
    this.renderCalendar();
  }

  changeCalendar(newMonth, newYear) {
    const date = new Date(newYear, newMonth);
    // Update the state
    this.dateState.year = date.getFullYear();
    this.dateState.month = date.getMonth();
    this.renderCalendar();
  }

  focusOnField(event) {
    this.element.querySelectorAll(".calendar__input").forEach((input) => {
      input.classList.remove("calendar__input--focus");
    });
    event.target.classList.add("calendar__input--focus");
  }

  removeSelectedDateHighlight() {
    this.element
      .querySelectorAll(".calendar__date--selected")
      .forEach((selected) => {
        selected.classList.remove("calendar__date--selected");
      });
  }

  removeDateRangeHighlight() {
    this.element
      .querySelectorAll(".calendar__date--in-between")
      .forEach((selected) => {
        selected.classList.remove("calendar__date--in-between");
      });
  }
}

// Remove this line if this component is not intended
// to initialize on DOM load.
Calendar.init();
